import { FullCLesson } from '@yandex-int/k-common/typings';
import { Text } from 'platform-components';
import { INPUT_THEMES } from 'platform-components/src/components/constants';
// @ts-ignore
import InputNumber from 'platform-components/src/components/input-number/input-number';
import { InputChangeEvent } from 'platform-components/src/typings';
import React from 'react';
import { IntlShape, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { LESSON_DATES } from 'common.components/constants/lesson';
import LessonDateSelect, { Option as LessonDateOption } from 'common.components/lesson-date-select/lesson-date-select';
import * as lessonSelectors from 'common.components/lesson/selectors';
import withTime, { InjectedProps as WithNowProps } from 'common.components/timer/withTime';
import { LessonDate } from 'utils/lesson/lesson-time/types';

import EvaluationDateHint from '../../__evaluation-date-hint/assignment__evaluation-date-hint';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import { getDefaultAssignDate, getDefaultDeadlineDate, formatDate } from '../../utils';

import './assignment__date-tab_control-work.scss';

interface DispatchProps {
    setAssignDate: typeof actions.setAssignDate;
    setDeadlineDate: typeof actions.setDeadlineDate;
    setDuration: typeof actions.setDuration;
}

interface StoreProps {
    assignDate: LessonDate | null;
    deadlineDate: LessonDate | null;
    deadlineDateValue: Date | null;
    assignDateError: string | null;
    deadlineDateError: string | null;
    duration: number | null;
    durationError: string | null;
    lesson: FullCLesson | null;
    isPreparedLesson: boolean;
    evaluationDate: Date | null;
    durationEditable?: boolean;
}

interface HOCProps extends WrappedComponentProps, WithNowProps {}

interface OwnProps {
    disabled: boolean;
    activeSubscriptionSlug: string | null;
}

interface Props extends OwnProps, StoreProps, DispatchProps, HOCProps {}

export const AssignmentDateTabControlWork = (props: Props) => {
    const {
        assignDate,
        deadlineDate,
        deadlineDateValue,
        assignDateError,
        deadlineDateError,
        intl,
        disabled,
        setAssignDate,
        setDeadlineDate,
        duration,
        setDuration,
        durationError,
        lesson,
        isPreparedLesson,
        evaluationDate,
        durationEditable,
        activeSubscriptionSlug,
    } = props;

    // eslint-disable-next-line complexity
    const handleChangeAssignmentDate = (value: LESSON_DATES, date: Date | null) => {
        let customDate: Date | null = null;
        const metrikaParams = {
            clessonId: lesson?.clesson?.id,
            lessonId: lesson?.id,
            contentType: isPreparedLesson ? 'preparedLesson' : 'courseLesson',
            subscription_slug: activeSubscriptionSlug,
        };
        if (value === LESSON_DATES.CUSTOM) {
            customDate = date ? date : getDefaultAssignDate();

            ym('reachGoal', 'teacher_lesson_creation_set_time_calendar', metrikaParams);
        } else {
            ym('reachGoal', 'teacher_lesson_creation_set_time', {
                optionNumber: getAssignDateOptions(intl).findIndex((item) => item.value === value),
                ...metrikaParams,
            });
        }

        setAssignDate({ type: value, customDate });
    };

    // eslint-disable-next-line complexity
    const handleChangeDeadlineDate = (value: LESSON_DATES, date: Date | null) => {
        let customDate: Date | null = null;
        if (value === LESSON_DATES.CUSTOM) {
            customDate = date ? date : getDefaultDeadlineDate();
        }

        setDeadlineDate({ type: value, customDate });

        const metrikaParams = {
            clessonId: lesson?.clesson?.id,
            lessonId: lesson?.id,
            contentType: isPreparedLesson ? 'preparedLesson' : 'courseLesson',
            subscription_slug: activeSubscriptionSlug,
        };

        if (value === LESSON_DATES.CUSTOM) {
            ym('reachGoal', 'lesson_deadline_calendar', metrikaParams);
        } else {
            ym('reachGoal', 'lesson_deadline_predefined', {
                optionNumber: getDeadlineDateOptions(intl).findIndex((item) => item.value === value),
                ...metrikaParams,
            });
        }
    };

    const handleChangeDuration = ({ target: { value } }: InputChangeEvent) => {
        setDuration(Number(value) || null);
    };

    const assignDateDivider = intl.formatMessage({ id: 'assignLessonModal.lessonDatePicker.fromDivider' });
    const deadlineDateDivider = intl.formatMessage({ id: 'assignLessonModal.lessonDatePicker.toDivider' });
    const durationPlaceholder = intl.formatMessage({ id: 'assignLessonModal.controlWork.duration.placeholder' });

    const shouldShowEvaluationDateHint = deadlineDateValue && duration && !durationError;

    return (
        <React.Fragment>
            <div className="assignment__start-date">
                <div className="assignment__label">
                    <Text id="assignLessonModal.controlWork.assignDate" />
                </div>
                <LessonDateSelect
                    customDate={assignDate?.customDate}
                    disabled={disabled}
                    errorMessage={assignDateError}
                    formatDate={(date: Date) => formatDate(date, assignDateDivider)}
                    onChange={handleChangeAssignmentDate}
                    options={getAssignDateOptions(intl)}
                    preventCrossing
                    value={assignDate?.type}
                />
            </div>
            <div className="assignment__deadline-date">
                <div className="assignment__label">
                    <Text id="assignLessonModal.controlWork.deadlineDate" />
                </div>
                <LessonDateSelect
                    customDate={deadlineDate?.customDate}
                    disabled={disabled}
                    errorMessage={deadlineDateError}
                    formatDate={(date: Date) => formatDate(date, deadlineDateDivider)}
                    onChange={handleChangeDeadlineDate}
                    options={getDeadlineDateOptions(intl)}
                    preventCrossing
                    value={deadlineDate?.type}
                />
            </div>
            {durationEditable && (
                <div>
                    <div className="assignment__label">
                        <Text id="assignLessonModal.controlWork.duration" />
                    </div>
                    <div>
                        <div className="assignment__duration-input-wrapper">
                            <InputNumber
                                className="assignment__duration-input qa_assignment-duration-input"
                                disabled={disabled}
                                maxLength={3}
                                onChange={handleChangeDuration}
                                placeholder={durationPlaceholder}
                                theme={INPUT_THEMES.BORDERLESS}
                                value={duration ? duration.toString() : ''}
                            />
                        </div>
                        <Text id="assignLessonModal.controlWork.duration.units" />
                    </div>
                    {durationError && (
                        <div className="assignment__duration-error qa_assignment-duration-error">
                            <Text>{durationError}</Text>
                        </div>
                    )}
                    {shouldShowEvaluationDateHint && <EvaluationDateHint evaluationDate={evaluationDate} />}
                </div>
            )}
        </React.Fragment>
    );
};

const mapStateToProps = (storeState: any, { now }: HOCProps & OwnProps): StoreProps => ({
    assignDate: selectors.getAssignDate(storeState),
    deadlineDate: selectors.getDeadlineDate(storeState),
    deadlineDateValue: selectors.getDeadlineDateValue(storeState, { now }),
    assignDateError: selectors.getAssignDateError(storeState),
    deadlineDateError: selectors.getDeadlineDateError(storeState),
    duration: selectors.getDuration(storeState),
    durationEditable: lessonSelectors.getIsDurationEditable(storeState),
    durationError: selectors.getDurationError(storeState),
    lesson: lessonSelectors.getLesson(storeState),
    isPreparedLesson: lessonSelectors.getIsPreparedLesson(storeState),
    evaluationDate: selectors.getEvaluationDate(storeState, { now }),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            setAssignDate: actions.setAssignDate,
            setDeadlineDate: actions.setDeadlineDate,
            setDuration: actions.setDuration,
        },
        dispatch
    );
};

const getAssignDateOptions = (intl: IntlShape): LessonDateOption[] => {
    return [
        {
            value: LESSON_DATES.AFTER_ASSIGN,
            label: intl.formatMessage({ id: 'assignLessonModal.datePresets.afterAssign' }),
        },
    ];
};

const getDeadlineDateOptions = (intl: IntlShape): LessonDateOption[] => {
    return [
        {
            value: LESSON_DATES.TOMORROW_MORNING,
            label: intl.formatMessage({ id: 'assignLessonModal.datePresets.tomorrowMorning' }),
        },
        {
            value: LESSON_DATES.IN_FIFTEEN_MINUTES,
            label: intl.formatMessage({ id: 'assignLessonModal.datePresets.inFifteenMinutes' }),
        },
    ];
};

export default injectIntl(withTime(connect(mapStateToProps, mapDispatchToProps)(AssignmentDateTabControlWork)));
